import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, rgba(15, 23, 42, 0.7) 0%, rgba(0, 0, 0, 0.85) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  opacity: 0;
  animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }

  @media (max-width: 850px) {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(15, 23, 42, 0.85));
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    align-items: center;
    justify-content: center;
    padding: 0;
    overflow: hidden;
  }
`;

const ModalContentWrapper = styled.div`
  /* Glassmorphic background - same as apartment list */
  background: linear-gradient(135deg, 
    rgba(248, 250, 252, 0.7), 
    rgba(241, 245, 249, 0.4),
    rgba(226, 232, 240, 0.2)
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.3),
    0 8px 24px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  width: 90%;
  max-width: 920px;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
  transform: scale(0.9) translateY(20px);
  animation: modalIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  border: 1px solid rgba(226, 232, 240, 0.6);
  
  /* Add subtle pattern overlay - same as apartment list */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 20px 20px;
    background-position: 0 0, 10px 10px;
    pointer-events: none;
    z-index: 0;
    border-radius: 20px;
  }
  
  @keyframes modalIn {
    to {
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 850px) {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    border-radius: 0;
    transform: translateY(100%);
    animation: slideUpMobile 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    display: flex;
    flex-direction: column;
    box-shadow: 
      0 -8px 32px rgba(0, 0, 0, 0.12),
      0 -4px 16px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: none;
    position: relative;


    @keyframes slideUpMobile {
      to {
        transform: translateY(0);
      }
    }
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  position: relative;
  z-index: 1;

  /* Hide scrollbar on desktop while keeping scroll functionality */
  &::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
  
  /* For Firefox */
  scrollbar-width: none;
  
  /* For Internet Explorer and Edge */
  -ms-overflow-style: none;

  @media (max-width: 850px) {
    padding: 24px;
    padding-top: 32px;
    padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    
    /* Custom scrollbar for mobile */
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(135deg, #2563eb, #1e40af);
    }
  }
`;

const CloseButton = styled.button`
  background: #f1f3f4;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 24px;
  font-weight: 300;
  color: #5f6368;
  transition: all 0.2s ease;
  z-index: 10;
  position: absolute;
  top: 20px;
  right: 20px;
  line-height: 1;
  padding: 0;
  margin: 0;
  text-align: center;
  transform-origin: center center;
  box-sizing: border-box;

  &:hover {
    background: #e8eaed;
    transform: rotate(90deg);
    color: #374151;
  }

  &:active {
    transform: rotate(90deg) scale(0.95);
    transition: transform 0.1s ease;
  }

  @media (max-width: 850px) {
    width: 48px;
    height: 48px;
    font-size: 26px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    color: #374151;
    font-weight: 400;
    top: 24px;
    right: 24px;
    
    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: scale(0.95);
      transition: transform 0.1s ease;
    }
  }
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  // Prevent accidental closes: only the explicit X button closes the modal.
  // (No backdrop-click and no swipe-to-close — scrolling was triggering close on mobile.)
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const modalContent = (
    <ModalBackdrop onClick={stopPropagation} onTouchStart={stopPropagation}>
      <ModalContentWrapper onClick={stopPropagation}>
        <CloseButton onClick={onClose} aria-label="Schließen" type="button">
          ×
        </CloseButton>
        <ModalContent>
          {children}
        </ModalContent>
      </ModalContentWrapper>
    </ModalBackdrop>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;